import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import PropTypes from 'prop-types';
import {Component, Fragment} from 'react';
import ReduxStore from 'ReduxStore';
import {DateInput} from 'semantic-ui-calendar-react';
import { Form, Grid, Label, Popup, Segment } from 'semantic-ui-react';
import FilterFields from 'components/FilterFields';
import Slider from 'components/Slider';
import {updateFilters} from 'ducks/filters';
import {changeUserSettings} from 'ducks/userSettings';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import NumberValidator from 'lib/NumberValidator';
import {isNZLocale} from 'lib/PropertyUtils';
import 'components/Filters/Filters.scss';
import {getShowActiveListingsRentals} from 'actionCreators/comparableItems';
import RangeValidator from 'lib/RangeValidator';

export const getErrorsFromFilters = (filters = {}) => {
  const withinDateFrom = RangeValidator.isDateValid(filters?.withinDateFrom) ||
      RangeValidator.validateDateFromTo('from', filters?.withinDateFrom, filters?.withinDateTo);
  const withinDateTo = RangeValidator.isDateValid(filters?.withinDateTo) ||
      RangeValidator.validateDateFromTo('to', filters?.withinDateFrom, filters?.withinDateTo);
  const errors = {
    landAreaMin: RangeValidator.validateMinMax('min', filters?.landAreaMin, filters?.landAreaMax),
    landAreaMax: RangeValidator.validateMinMax('max', filters?.landAreaMin, filters?.landAreaMax),
    priceMin: RangeValidator.validateMinMax('min', filters?.priceMin, filters?.priceMax),
    priceMax: RangeValidator.validateMinMax('max', filters?.priceMin, filters?.priceMax),
    withinDateFrom: filters?.toggleWithinDate ? withinDateFrom : '',
    withinDateTo: filters?.toggleWithinDate ? withinDateTo : '',
  };
  return Object
      .keys(filters)
      .reduce((accum, filterKey) =>
          errors[filterKey]
              ? { ...accum, [filterKey]: errors[filterKey] }
              : accum, {});
};

export class Filters extends Component {
  static propTypes = {
    featureFlags: PropTypes.shape({
      rapidSearchEnabled: PropTypes.bool,
    }),
    fetching: PropTypes.bool.isRequired,
    filters: PropTypes.shape({
      propertyTypes: PropTypes.array.isRequired,
      radius: PropTypes.string.isRequired,
      targetSuburb: PropTypes.bool.isRequired,
      withinMonths: PropTypes.string.isRequired,
      landAreaMin: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      landAreaMax: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      priceMin: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      priceMax: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      beds: PropTypes.string.isRequired,
      baths: PropTypes.string.isRequired,
      carSpaces: PropTypes.string.isRequired,
      errors: PropTypes.object.isRequired,
    }).isRequired,
    pageContext: PropTypes.string,
    propertyId: PropTypes.number.isRequired,
    propertyDetail: PropTypes.shape({
      localityId: PropTypes.number,
      localityName: PropTypes.string,
      state: PropTypes.string,
    }),
    propertyDetailCache: PropTypes.shape({
      addressTown: PropTypes.string,
    }),
    reportType: PropTypes.shape({
      value: PropTypes.string,
    }),
    userSettings: PropTypes.object,
  };

  constructor(props) {
    super(props);
    const {filters} = this.props;
    this.state = {filters};
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.filters, prevProps.filters) || this.props.reportType.value !== prevProps.reportType.value) {
      this.setState({filters: this.props.filters});
    }
  }

  _getShowActiveListingsRentals = () => {
    const { userSettings, pageContext } = this.props;
    return getShowActiveListingsRentals({
      userSettings,
      comparableType: pageContext
    });
  };

  onDateChange = (fieldName) => {
    return (e, {value, checked}) => {
      const filters = this.state.filters || {};
      const getValue = givenField => fieldName === givenField ? value : filters[givenField];
      const isCustomDateChecked = checked !== undefined ? checked : filters?.toggleWithinDate;
      const errors = getErrorsFromFilters({
        withinDateFrom: getValue('withinDateFrom'),
        withinDateTo: getValue('withinDateTo'),
        toggleWithinDate: isCustomDateChecked,
      });
      this.onFilterFieldChange(fieldName)(e, {value, checked, errors: {
          ...filters.errors,
          withinDateFrom: errors.withinDateFrom,
          withinDateTo: errors.withinDateTo,
          toggleWithinDate: errors.toggleWithinDate,
        }});
    };
  };

  onFilterFieldChange = (fieldName) => {
    return (e, {value, checked, errors: newErrors}) => {
      if (checked === !!checked) {
        value = checked;
      }

      const {errors} = this.state.filters;
      this.setState(({filters}) => {
        const newFilters = cloneDeep(filters);
        newFilters[fieldName] = value;
        newFilters.errors = { ...errors, ...newErrors };
        return ({filters: newFilters});
      });
    };
  };

  toIntOrEmptyString = (value) => {
    return !!value ? parseInt(value, 10) : '';
  };

  getNumValue = (value) => value === '' ? '' : Number(value);

  onNumericFilterFieldChange = (fieldName) => {
    return (e, {value, unit}) => {
      const filters = this.state.filters || {};
      // added the `!unit` condition to use the filters value instead of the converted `value` from LandSizeRange component
      // in the getErrorsFromFilters()
      const getValue = givenField => (fieldName === givenField && !unit) ? this.getNumValue(value) : filters[givenField];
      if (NumberValidator.isValidInteger(value, 9)) {
        const errors = getErrorsFromFilters({
          landAreaMin: getValue('landAreaMin'),
          landAreaMax: getValue('landAreaMax'),
        });
        this.onFilterFieldChange(fieldName)(e, {value: this.toIntOrEmptyString(value), errors: {
          ...filters.errors,
          landAreaMin: errors.landAreaMin,
          landAreaMax: errors.landAreaMax,
        }});
      }
    };
  };

  onPriceFilterFieldChange = (fieldName) => {
    return (e, {value}) => {
      const filters = this.state.filters || {};
      const getValue = givenField => fieldName === givenField ? this.getNumValue(value) : filters[givenField];
      if (NumberValidator.isValidInteger(value, 8)) {
        const errors = getErrorsFromFilters({
          priceMin: getValue('priceMin'),
          priceMax: getValue('priceMax'),
        });
        this.onFilterFieldChange(fieldName)(e, {value: this.toIntOrEmptyString(value), errors: {
            ...filters.errors,
            priceMin: errors.priceMin,
            priceMax: errors.priceMax,
          }});
      }
    };
  };

  onTargetSuburbChange = (e, {checked: targetSuburb}) => this.setState(({filters}) => ({
    filters: {
      ...filters,
      targetSuburb
    }
  }));

  onChangeShowCurrentListingsRentals = () => {
    const { pageContext, reportType } = this.props;
    const payload = {
      [`show.current.${pageContext}`]: !this._getShowActiveListingsRentals(),
    };
    ReduxStore.dispatch(changeUserSettings(payload, reportType.value));
  };

  onUpdateFiltersClick = (e) => {
    const {filters} = this.state;
    const {pageContext, propertyId} = this.props;
    ReduxStore.dispatch(updateFilters(filters, true));
    e.preventDefault();
    AnalyticsGateway.sendEvent({
      eventName: 'Refine Search',
      properties: [
        {label: 'Page', value: `Comparable ${capitalize(pageContext)}`},
        {label: 'Property ID', value: propertyId},
        {label: 'Property Types', value: !isEmpty(filters.propertyTypes) ? filters.propertyTypes : 'ANY'},
        {label: 'Sold Within', value: filters.withinMonths && `${filters.withinMonths} months`},
        {label: 'Radius', value: filters.radius && `${filters.radius} km`},
        {label: 'Target Suburb', value: filters.targetSuburb},
        {label: 'Bed', value: filters.beds || false},
        {label: 'Bath', value: filters.baths || false},
        {label: 'Car', value: filters.carSpaces || false},
        {label: 'Land Size Min', value: filters.landAreaMin || false},
        {label: 'Land Size Max', value: filters.landAreaMax || false},
        {label: 'Price Min', value: filters.priceMin || false},
        {label: 'Price Max', value: filters.priceMax || false},
        {label: 'Active Listing', value: this._getShowActiveListingsRentals() || false}
      ],
    });
  };

  slideChange = (fieldName) => {
    return (value) => {
      this.onFilterFieldChange(fieldName)({}, {value});
    };
  };

  render() {
    const {
      featureFlags: {
        rapidSearchEnabled
      },
      fetching,
      pageContext,
      propertyDetail,
      propertyDetailCache,
    } = this.props;
    const {
      filters: {
        propertyTypes,
        withinMonths,
        toggleWithinDate,
        withinDateFrom,
        withinDateTo,
        radius,
        targetSuburb,
        landAreaMin,
        landAreaMax,
        priceMin,
        priceMax,
        beds,
        baths,
        carSpaces,
        errors = {},
      }
    } = this.state;

    const isNz = isNZLocale();
    const missingSuburbTown = !propertyDetail?.localityName && !propertyDetailCache?.addressTown;
    const isUpdateFiltersDisabled = !!Object.keys(errors)?.some(fieldName => errors[fieldName]);

    const validatedDate = date => !RangeValidator.isDateValid(date) ? date : '';

    return (
        <>
          <Segment className="Filters filters" data-testid="Filters">
            <h3>Refine your search</h3>

            <Form>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={11}>
                    <Form.Group>
                      <FilterFields.PropertyType width={8}
                                                 propertyTypes={propertyTypes}
                                                 onChange={this.onFilterFieldChange('propertyTypes')}/>
                      <FilterFields.Radius radius={radius} onChange={this.onFilterFieldChange('radius')}/>

                      <Popup trigger={
                        <Form.Checkbox id="target-suburb"
                                       disabled={rapidSearchEnabled && isNz && missingSuburbTown}
                                       label="Target suburb"
                                       checked={targetSuburb}
                                       className="target-suburb"
                                       onChange={this.onTargetSuburbChange}/>
                      }>
                        <Popup.Content>
                          Include only {capitalize(propertyDetail.localityName)} results
                        </Popup.Content>
                      </Popup>
                    </Form.Group>
                    <Form.Group>
                      {toggleWithinDate ? (
                          <Fragment>
                            <Form.Field>
                              <DateInput
                                  label={(pageContext === 'sales' ? 'Sold within' : 'Listed within') + (errors.withinDateFrom ? ' *(dates required)' : '')}
                                  className={`date-input-from ${errors.withinDateFrom ? 'error' : ''}`}
                                  startMode="year"
                                  placeholder="From Date"
                                  value={withinDateFrom || ''}
                                  iconPosition="left"
                                  dateFormat="YYYY-MM-DD"
                                  maxDate={validatedDate(withinDateTo) || moment().format('YYYY-MM-DD')}
                                  onChange={this.onDateChange('withinDateFrom')}
                                  closable
                              />
                              {errors.withinDateFrom ? <Label basic color="red" pointing>{errors.withinDateFrom}</Label>: null}
                            </Form.Field>
                            <div className="dash">–</div>
                            <Form.Field>

                              <DateInput
                                  label="&nbsp;"
                                  className={`date-input-to ${errors.withinDateTo ? 'error' : ''}`}
                                  startMode="year"
                                  placeholder="To Date"
                                  value={withinDateTo || ''}
                                  iconPosition="left"
                                  dateFormat="YYYY-MM-DD"
                                  minDate={validatedDate(withinDateFrom)}
                                  maxDate={moment().format('YYYY-MM-DD')}
                                  onChange={this.onDateChange('withinDateTo')}
                                  closable
                              />
                              {errors.withinDateTo ? <Label basic color="red" pointing>{errors.withinDateTo}</Label>: null}
                            </Form.Field>
                          </Fragment>
                      ) : (
                          <Fragment>
                            {pageContext === 'sales' && (
                                <FilterFields.SoldWithin width={3} withinMonths={withinMonths}
                                                         onChange={this.onFilterFieldChange('withinMonths')}/>
                            )}
                            {['listings', 'rentals'].includes(pageContext) && (
                                <FilterFields.ListedWithin width={3} withinMonths={withinMonths}
                                                           onChange={this.onFilterFieldChange('withinMonths')}/>
                            )}
                          </Fragment>
                      )}
                      <Form.Checkbox className="toggle-within" label="Custom date" checked={toggleWithinDate}
                                     onChange={this.onDateChange('toggleWithinDate')}/>
                      {(pageContext === 'listings' || pageContext === 'rentals') &&
                          (<Popup className="toggle-active-popup" trigger={
                            <Form.Checkbox label={`Show only current ${pageContext}`} checked={this._getShowActiveListingsRentals()}
                                           className="toggle-active"
                                           onChange={this.onChangeShowCurrentListingsRentals}/>
                          }
                          >
                            <Popup.Content>
                              Excludes listings known to be removed from the market.
                            </Popup.Content>
                          </Popup>)
                      }
                    </Form.Group>
                    <Form.Group>
                      <FilterFields.LandSizeRange
                          min={landAreaMin} max={landAreaMax} errors={errors}
                          onMinChange={this.onNumericFilterFieldChange('landAreaMin')}
                          onMaxChange={this.onNumericFilterFieldChange('landAreaMax')}/>
                      <FilterFields.SalePriceMin width={3} value={priceMin} errors={errors.priceMin}
                                                 onChange={this.onPriceFilterFieldChange('priceMin')}/>
                      <FilterFields.SalePriceMax width={3} value={priceMax} errors={errors.priceMax}
                                                 onChange={this.onPriceFilterFieldChange('priceMax')}/>
                    </Form.Group>
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <Slider className="beds" label="Beds" max={6} value={beds} onChange={this.slideChange('beds')}/>
                    <Slider className="baths" label="Baths" max={6} value={baths} onChange={this.slideChange('baths')}/>
                    <Slider className="cars" label="Cars" max={6} value={carSpaces}
                            onChange={this.slideChange('carSpaces')}/>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <FilterFields.UpdateFilters fetching={fetching} errors={fetching ? null : errors.request} disabled={isUpdateFiltersDisabled}
                                          onClick={this.onUpdateFiltersClick}/>
            </Form>
          </Segment>
        </>
    );
  }
}

export default Filters;
